import { Block } from '../classes/Block';
import { VideoSettings } from '../components/VideoSettings';
import { CardGroup, buildBlockCardIndexMap } from './cardGroups';
import { DefaultCardSettings } from './defaultCardSettings';

export type TableRow = Array<string>;
export type TableMatrix = TableRow[];

export interface MinimalColumnDef {
  field: string;
}

export function buildTableData(
  blocksInput: Block[],
  columns: MinimalColumnDef[],
  settings: VideoSettings,
  cardGroups?: CardGroup[],
  defaultCardSettings?: DefaultCardSettings
): TableMatrix {
  const result: TableMatrix = [];

  const settingsEntries: Array<[string, unknown]> = Object.entries(settings)
    .filter(([key]) => key !== 'card_framing' || settings.mode === 'cards');
  if (defaultCardSettings) {
    settingsEntries.push(['default_card_settings', defaultCardSettings]);
  }

  const serializedSettingsEntries: Array<string> = [];
  for (const [key, value] of settingsEntries) {
    const outValue: string = (key === 'fonts' || key === 'logos' || key === 'default_card_settings' || key === 'card_framing')
      ? JSON.stringify(value ?? {})
      : String(value ?? '');
    serializedSettingsEntries.push(key, outValue);
  }

  const videoSettingsRow: TableRow = ['video_settings', ...serializedSettingsEntries];
  result.push(videoSettingsRow);

  const blockCardIndexMap =
    settings.mode === 'cards' && cardGroups && cardGroups.length > 0
      ? buildBlockCardIndexMap(cardGroups)
      : null;

  for (const block of blocksInput) {
    if (!block || !block.name) {
      // eslint-disable-next-line no-console
      console.warn('ExcelExporter: Skipping block due to missing name.');
      continue;
    }

    const blockObject: Record<string, unknown> = {
      show_block_name: block.shouldDisplayBlockName,
      block_name_font_style: block.blockNameFont,
      block_name_font_size: block.blockNameSize,
      block_name_font_weight: block.blockNameWeight,
      block_name_font_width: block.blockNameFontWidth,
      block_name_color: block.blockNameColor,
      block_name_alignment: block.blockNameAlignment ?? 'center',
      column_num: block.numColumns,
      distance_below_block_name: block.distanceBelowBlockName,
      label_value_gap: block.labelValueGap,
      margin_column: block.marginColumn ?? 45,
      edge_distance: block.edgeDistance ?? 0,
      distance_between_rows: block.distanceBetweenRows,
      distance_from_previous: block.distanceFromPreviousBlock,
      song_block: block.isSongBlock,
      single_cell: block.isSingleCell,
      credits_list: block.isCreditsList,
      credits_list_separator: block.separator,
      logo_block: block.isLogoBlock,
      sbs_blocks: block.sbsBlocks ?? 'normal',
      sbs_distance: block.sbsDistance ?? 0,
      label_font_name: block.labelFontName,
      label_font_size: block.labelFontSize,
      label_font_weight: block.labelFontWeight,
      label_font_width: block.labelFontWidth,
      label_align: block.labelAlign,
      label_vertical_align: block.labelVerticalAlign,
      label_color: block.labelFontColor,
      value_font_name: block.valueFontName,
      value_font_size: block.valueFontSize,
      value_font_weight: block.valueFontWeight,
      value_font_width: block.valueFontWidth,
      value_align: block.valueAlign,
      value_vertical_align: block.valueVerticalAlign,
      value_color: block.valueFontColor,
      has_separate_value_formatting: block.hasSeparateValueFormatting,
      label_value_alignment: block.labelValueAlignment ?? 'horizontal',
      card_fade_in_frames: block.cardFadeInFrames ?? 0,
      card_duration_fully_up_frames: block.cardDurationFullyUpFrames ?? 0,
      card_fade_out_frames: block.cardFadeOutFrames ?? 0,
      card_slug_frames: block.cardSlugFrames ?? 0,
      card_override_full_width: block.cardOverrideFullWidth ?? false,
      card_horizontal_offset_px: block.cardHorizontalOffsetPx ?? 0,
      exchange_label_value: block.exchangeLabelValue ?? false,
      block_locked: block.isLocked,
      card_timing_locked: block.cardTimingLocked ?? false,
    };

    if (blockCardIndexMap !== null) {
      const cardIndex = blockCardIndexMap.get(block.id);
      if (cardIndex !== undefined) {
        blockObject.card_index = cardIndex;
      }
    }

    const blockDefinitionRow: TableRow = [block.name];
    for (const [key, value] of Object.entries(blockObject)) {
      blockDefinitionRow.push(key, String(value ?? ''));
    }
    result.push(blockDefinitionRow);

    if (block.dataRows && block.dataRows.length > 0) {
      for (const dataRow of block.dataRows) {
        const actualDataValues: TableRow = [];
        for (const colDef of columns) {
          const value = (dataRow as Record<string, string | number>)[colDef.field];
          actualDataValues.push(String(value ?? ''));
        }
        result.push(['', ...actualDataValues]);
      }
    }
  }

  return result;
}
